// src/pages/Careers.jsx
import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ApplyModal from '../components/ApplyModal';
import Reveal from '../components/Reveal';
import { FALLBACK_JOBS } from '../data/careersData';

import careerHeroImg from '../assets/images/careers-hero.jpg';

function BriefcaseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );
}

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeJob, setActiveJob] = useState(null);

  useEffect(() => {
    async function fetchJobs() {
      try {
        const q = query(collection(db, 'jobs'), orderBy('title'));
        const snap = await getDocs(q);
        const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setJobs(fetched.length > 0 ? fetched : FALLBACK_JOBS);
      } catch {
        setJobs(FALLBACK_JOBS);
      } finally {
        setLoading(false);
      }
    }
    fetchJobs();
  }, []);

  return (
    <>
      <SEO
        title="Careers — Join Our Team"
        description="Career opportunities at DistrictSourcingTech supporting active service delivery and workforce scaling across government and enterprise environments."
        url="https://districtsourcingtech.com/careers"
        keywords="District Sourcing Tech careers, IT jobs, government IT careers, workforce jobs, QA engineer, UX designer jobs"
      />

      <Navbar variant="light" />

      <section className="career-hero">
        <div className="career-hero__bg">
          <img src={careerHeroImg} alt="Team collaborating" />
        </div>
        <div className="career-hero__overlay" />
        <div className="career-hero__card">
          <p className="career-hero__label">Career</p>
          <h1 className="title-1" style={{ marginBottom: '8px', marginTop: '4px' }}>Join us</h1>
          <p className="body-lg" style={{ color: 'var(--token-desc)', marginBottom: '32px' }}>
            Career opportunities at DistrictSourcingTech support active service delivery and workforce scaling across government and enterprise environments.
          </p>
          <a
            href="#openings"
            className="btn btn-primary"
            onClick={(e) => { e.preventDefault(); document.getElementById('openings')?.scrollIntoView({ behavior: 'smooth' }); }}
          >
            View openings
          </a>
        </div>
      </section>

      <section className="section section--alt" id="openings">
        <div style={{ maxWidth: '1376px', margin: '0 auto' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 className="title-2">Current job openings</h2>
            <p className="body-lg" style={{ color: 'var(--token-desc)', marginTop: '12px' }}>
              Click on a role to learn more, or apply directly.
            </p>
          </Reveal>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--token-desc)' }}>
              <span className="spinner" style={{ borderColor: 'rgba(0,0,0,0.1)', borderTopColor: 'var(--primary-500)', margin: '0 auto 16px', display: 'block' }} />
              Loading opportunities...
            </div>
          ) : (
            <div className="jobs-grid">
              {jobs.map((job, i) => (
                <Reveal key={job.id} delay={i * 100} className="job-card">
                  <Link to={`/careers/${job.id}`} className="job-card__title-link">
                    <h3 className="title-5 job-card__title">{job.title}</h3>
                  </Link>
                  <p className="body-lg" style={{ color: 'var(--token-desc)' }}>{job.desc || job.description}</p>
                  <div className="job-card__meta">
                    <div className="job-card__meta-item"><BriefcaseIcon /><span>{job.type || job.period}</span></div>
                    <div className="job-card__meta-item"><UserIcon /><span>{job.level}</span></div>
                    {job.deadline && (
                      <div className="job-card__meta-item"><CalendarIcon /><span>Deadline: {job.deadline}</span></div>
                    )}
                  </div>
                  {job.tags && (
                    <div className="service-tags">
                      {(typeof job.tags === 'string' ? job.tags.split(',') : job.tags)
                        .map(t => t.trim()).filter(Boolean)
                        .map(t => <span key={t} className="service-tag">{t}</span>)}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '12px', marginTop: 'auto' }}>
                    <Link to={`/careers/${job.id}`} className="btn btn-outline" style={{ flex: 1, textAlign: 'center' }}>
                      View details
                    </Link>
                    <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => setActiveJob(job)}>
                      Apply now
                    </button>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />

      {activeJob && (
        <ApplyModal job={activeJob} onClose={() => setActiveJob(null)} />
      )}
    </>
  );
}
